
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { GameProvider } from './contexts/GameContext'
import { WebSocketProvider } from './contexts/WebSocketContext'
import { TournamentProvider } from './contexts/TournamentContext'
import { NotificationProvider, useNotifications } from './contexts/NotificationContext'
import { AudioProvider } from './contexts/AudioContext'

import AuthPage from './components/Auth/AuthPage'
import ResetPassword from './components/Auth/ResetPassword'
import VerifyEmail from './components/Auth/VerifyEmail'
import ToastContainer from './components/Toast/Toast'
import Toaster from './components/Toaster/Toaster'

import Layout from './components/Layout/Layout'
import LandingPage from './components/LandingPage/LandingPage'

// Legacy Dashboard.js.old Order:
import GamePage from './pages/GamePage' // 1. Game
import Profile from './components/Profile/Profile' // 2. Profile
import PublicProfile from './components/PublicProfile/PublicProfile' // 3. PublicProfile
import Leaderboard from './components/Leaderboard/Leaderboard' // 4. Leaderboard
import Rules from './components/Rules/Rules' // 5. Rules
import GamePreferences from './components/GamePreferences/GamePreferences' // 6. GamePreferences
import WhyRiftChess from './components/WhyRiftChess/WhyRiftChess' // 7. WhyRiftChess
import Tournaments from './components/Tournaments/Tournaments' // 8. Tournaments
import TournamentDetails from './components/TournamentDetails/TournamentDetails' // 9. TournamentDetails
import Replay from './components/Replay/Replay' // 10. Replay
import ReplayImporter from './components/ReplayImporter/ReplayImporter' // 11. ReplayImporter
import ComingSoon from './components/ComingSoon/ComingSoon' // 12. ComingSoon
import Premium from './components/Premium/Premium' // 13. Premium
import Friends from './components/Friends/Friends' // 14. Friends
import Notifications from './components/Notifications/Notifications' // 15. Notifications
import PlatformStats from './components/PlatformStats/PlatformStats' // 16. PlatformStats
import FairPlay from './components/FairPlay/FairPlay' // 17. FairPlay
import AdminPage from './pages/AdminPage' // 18. AdminPage
import SpectateGamesList from './pages/SpectateGamesList' // 19. SpectateGamesList
import DashboardHome from './components/DashboardHome/DashboardHome' // 20. MatchmakingDashboard (inside DashboardHome)

import './App.css'

function NotificationToaster() {
  const { 
    notifications, 
    acceptChallenge, 
    declineChallenge, 
    acceptTournamentInvite,
    declineTournamentInvite,
    removeNotification 
  } = useNotifications()

  const handleAccept = (notification) => {
    if (notification.type === 'tournament') {
      acceptTournamentInvite(notification)
    } else {
      acceptChallenge(notification)
    }
  }

  const handleDecline = (notification) => {
    if (notification.type === 'tournament') {
      declineTournamentInvite(notification)
    } else {
      declineChallenge(notification)
    }
  }

  return (
    <Toaster
      notifications={notifications}
      onAccept={handleAccept}
      onDecline={handleDecline}
      onDismiss={removeNotification}
    />
  )
}

// Main Routing Component
function AppRoutes() {
  const { user, loading } = useAuth()
  
  if (loading) {
    return (
      <div className="loading-screen" style={{
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh', 
        backgroundColor: '#0F172A',
        color: '#fff'
      }}>
        Loading RiftChess...
      </div>
    )
  }

  // Unauthenticated Routes
  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/auth" element={<AuthPage />} />
        {/* Redirect any other route to Landing Page */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    )
  }

  // Authenticated Routes
  return (
      <Routes>
        <Route element={<Layout />}>
          <Route index element={<DashboardHome />} />
          <Route path="game/:id" element={<GamePage />} />
          
          <Route path="tournaments" element={<TournamentsWrapper />} />
          <Route path="tournaments/:id" element={<TournamentDetailsWrapper />} />
          
          <Route path="leaderboard" element={<LeaderboardWrapper />} />
          <Route path="profile" element={<ProfileWrapper />} />
          <Route path="user/:username" element={<PublicProfileWrapper />} />
          
          <Route path="replay" element={<ReplayImporterWrapper />} />
          <Route path="replay/:gameId/:moveNumber?" element={<ReplayWrapper />} />
          
          <Route path="spectate" element={<SpectateWrapper />} />
          <Route path="friends" element={<FriendsWrapper />} />
          <Route path="notifications" element={<NotificationsWrapper />} />
          <Route path="preferences" element={<GamePreferences />} />
          
          <Route path="rules" element={<Rules />} />
          <Route path="fairplay" element={<FairPlay />} />
          <Route path="why-riftchess" element={<WhyRiftChessWrapper />} />
          <Route path="stats" element={<PlatformStats />} />
          <Route path="premium" element={<PremiumWrapper />} />
          
          <Route path="admin" element={<AdminPage />} />
          
          <Route path="teams" element={<ComingSoon feature="Teams" onBack={() => window.history.back()} />} />
          <Route path="quests" element={<ComingSoon feature="Quests" onBack={() => window.history.back()} />} />
          
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
  )
}

// Wrappers to handle onBack props that some components expect
function TournamentsWrapper() {
  const navigate = useNavigate()
  return <Tournaments 
    initialTab="big" 
    onShowTournamentDetails={(id, tab) => navigate(`/tournaments/${id}`)} 
    onNavigateToAuth={() => {}} // Handle auth nav if needed or let it be 
  />
}

function TournamentDetailsWrapper() {
    const { id } = useParams()
    const navigate = useNavigate()
    return <TournamentDetails 
            tournamentId={id} 
            onBack={() => navigate('/tournaments')}
            onNavigateToGame={(gid) => navigate(`/game/${gid}`)}
            onNavigateToAuth={() => {}} 
            onViewProfile={(username) => navigate(`/user/${username}`)}
        />
}

function LeaderboardWrapper() {
    const navigate = useNavigate()
    return <Leaderboard onViewProfile={(username) => navigate(`/user/${username}`)} />
}

function ProfileWrapper() {
    const navigate = useNavigate()
    return <Profile onBack={() => navigate('/')} onViewReplay={(gid, move) => navigate(`/replay/${gid}/${move || 0}`)} />
}

function PublicProfileWrapper() {
    const { username } = useParams()
    const navigate = useNavigate()
    return <PublicProfile 
        username={username} 
        onBack={() => navigate(-1)} 
        onViewReplay={(gid, move) => navigate(`/replay/${gid}/${move || 0}`)} 
    />
}

function ReplayImporterWrapper() {
    const navigate = useNavigate()
    return <ReplayImporter onBack={() => navigate('/')} />
}

function ReplayWrapper() {
    const { gameId, moveNumber } = useParams()
    const navigate = useNavigate()
    return <Replay 
        gameId={gameId} 
        moveNumber={moveNumber ? parseInt(moveNumber) : 0} 
        onBack={() => navigate('/')} 
    />
}

function SpectateWrapper() {
    return <SpectateGamesList />
}

function FriendsWrapper() {
    const navigate = useNavigate()
    return <Friends onBack={() => navigate('/')} />
}

function NotificationsWrapper() {
    const navigate = useNavigate()
    // We need to fetch count? Dashboard did it. NotificationContext handles it mostly?
    // NotificationProvider exposes functionality. 
    return <Notifications onBack={() => navigate('/')} onNotificationUpdate={() => window.dispatchEvent(new Event('refresh-notifications'))} />
}

function PremiumWrapper() {
    const navigate = useNavigate()
    return <Premium onBack={() => navigate('/')} />
}

function WhyRiftChessWrapper() {
    const navigate = useNavigate()
    return <WhyRiftChess onNavigateToDashboard={() => navigate('/')} onNavigateToRules={() => navigate('/rules')} />
}


function AppContent() {
  const { loading } = useAuth()

  if (loading) {
    return (
      <div className="loading-container">
        <div className="app-loading-spinner">Loading...</div>
      </div>
    )
  }

  return (
    <Routes>
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="*" element={<AppRoutes />} />
    </Routes>
  )
}

function App() {
  return (
    <BrowserRouter>
        <AuthProvider>
        <div className="App">
            <AudioProvider>
            <WebSocketProvider>
                <GameProvider>
                <NotificationProvider>
                    <TournamentProvider>
                    <AppContent />
                    <NotificationToaster />
                    <ToastContainer />
                    </TournamentProvider>
                </NotificationProvider>
                </GameProvider>
            </WebSocketProvider>
            </AudioProvider>
        </div>
        </AuthProvider>
    </BrowserRouter>
  )
}

export default App